// @ts-expect-error - TS2614 - Module '"src/utils/date-time"' has no exported member 'Range'. Did you mean to use 'import Range from "src/utils/date-time"' instead?
import type {Range} from 'src/utils/date-time';
import type {EntityFilter} from 'src/types/ats-settings';

import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {Link} from 'src/rerouter';
import startCase from 'lodash/startCase';
import chunk from 'lodash/chunk';
import groupBy from 'lodash/groupBy';

import {classify, formatCurrency} from 'src/utils';
import {prettyBullhornLabel} from 'src/utils/entities';
import {useMultiEntityEnabled} from 'src/hooks/useAgencyConfig';
import {useReleaseFlag} from 'src/hooks/product-flags';

import {FilterCard} from './';
import {StringSelectList} from 'src/components/lib/token-list-input';
import {
  ValueListInput,
  type ValueListInputProps,
} from 'src/components/lib/token-list-input/new-stuff';
import {SimpleDateRangePicker} from 'src/components/lib/date-range-picker/date-range-picker';
import Slider from 'src/components/lib/slider';
import TypeaheadSelectList from 'src/components/typeahead/select-list';

import css from './more-filters-card.css';


type Props = {
  filters: EntityFilter[];
  onSave?: (e: React.SyntheticEvent<any>) => unknown;
  onCancel?: (e: React.SyntheticEvent<any>) => unknown;
  query?: Record<any, any>;
  onValueChange: (key: string) => <T>(value: T) => unknown;
  showSettingsLink: string | null | undefined;
  filteredFromAddresses?: string[];
  onChangeFromAddress?: (values: string[]) => unknown;
  showInternalUserFilter?: boolean;
  direction?: 'right' | 'left';
};

export default function MoreFiltersCard(props: Props) {
  const {
    filters,
    onSave,
    onCancel,
    onValueChange,
    showSettingsLink,
    onChangeFromAddress,
    filteredFromAddresses,
    showInternalUserFilter,
    direction = 'right',
  } = props;
  const {t, makeKey} = useLabelTranslation();

  const multiEntityEnabled = useMultiEntityEnabled();
  const showSentFrom = useReleaseFlag('fromAddressFilter');

  const columns = React.useMemo(() => {
    const sortedFilters = filters
      .slice()
      .sort(({entity_type: a}, {entity_type: b}) => a.localeCompare(b));

    return chunk(
      sortedFilters.map((filter, index, filters) => ({
        filter,
        lastOfType:
          (filters[index + 1] &&
            filter.entity_type !== filters[index + 1].entity_type) ||
          filters.length - 1 === index,
      })),
      Math.ceil(sortedFilters.length / 4),
    );
  }, [filters]);

  return (
    <FilterCard
      className={classify(css.containerWrap, {
        [css.left]: direction === 'left',
      })}
      header={t('more_filters', 'more filters')}
      onSave={onSave}
      onCancel={onCancel}
    >
      {!showInternalUserFilter &&
        showSentFrom &&
        onChangeFromAddress &&
        filteredFromAddresses != null && (
          <div>
            <div className={css.section}>
              <h5 className={css.entityFilterType}>
                {t('Sent_From', 'Sent From')}
              </h5>
              <TypeaheadSelectList
                typeaheadType="eventResources"
                typeaheadName="sent_event"
                typeaheadSubname="from_address"
                placeholder={t('from_email_address', 'from email address')}
                classNames={{
                  outerContainer: css.selectListContainer,
                  container: css.input,
                }}
                values={filteredFromAddresses}
                onChange={onChangeFromAddress}
              />
            </div>
          </div>
        )}
      <div className={css.columnWrap}>
        {columns.length === 0 && (
          <section className={classify(css.section, css.center)}>
            <h4 className={css.emptyTitle}>
              {t('No_Filters_Yet', 'No Filters Yet')}
            </h4>
            <span className={css.emptyText}>
              {t(
                'Create_your_own_custom_filters_by',
                'Create your own custom filters by',
              )}
              <br />
              {t(
                'adding_them_on_the_ATS_Settings_page',
                'adding them on the ATS Settings page',
              )}
            </span>
            {showSettingsLink && (
              <Link
                to={`/settings/ats/fields/${showSettingsLink}`}
                className={css.settingsLink}
              >
                {t('Add_Filter_in_Settings', 'Add Filter in Settings')}
              </Link>
            )}
          </section>
        )}
        {columns.map((column, index) => {
          const groupedByEntity = groupBy(column, 'filter.entity_type');
          return (
            <div className={css.column} key={index}>
              {Object.keys(groupedByEntity).map((entityGroup) => (
                <div className={css.section} key={entityGroup}>
                  <h5 className={css.entityFilterType}>
                    {t(
                      makeKey(
                        `${
                          //prettier-ignore
                          groupedByEntity[entityGroup][0]?.filter
                          // @ts-expect-error - TS2339 - Property 'entity_type_display_name' does not exist on type 'EntityFilter'.
                            ?.entity_type_display_name ||
                          prettyBullhornLabel(entityGroup)
                        } fields`,
                      ),
                      `${
                        //prettier-ignore
                        groupedByEntity[entityGroup][0]?.filter
                        // @ts-expect-error - TS2339 - Property 'entity_type_display_name' does not exist on type 'EntityFilter'.
                          ?.entity_type_display_name ||
                        prettyBullhornLabel(entityGroup)
                      } fields`,
                    )}
                  </h5>
                  {groupedByEntity[entityGroup].map(
                    ({filter: filterInfo, lastOfType}) => {
                      const {display_name, attribute_name} = filterInfo;
                      const filterName = `${entityGroup}/${attribute_name}`;
                      const editSettingsLink =
                        showSettingsLink && lastOfType ? (
                          <div className={css.actionRow}>
                            <Link
                              to={`/settings/ats/fields/${entityGroup}`}
                              className={css.settingsLink}
                            >
                              {`${t('Edit', 'Edit')} ${prettyBullhornLabel(
                                entityGroup,
                              )} ${t(
                                'Fields_in_Settings',
                                'Fields in Settings',
                              )}`}
                            </Link>
                          </div>
                        ) : null;

                      let filter;
                      if (filterInfo.data_type === 'currency') {
                        filter = (
                          <Slider
                            className={css.filter}
                            min={filterInfo.min}
                            max={filterInfo.max}
                            {...filterInfo.selected}
                            // @ts-expect-error - TS2322 - Type '(val: number, currency: string | null | undefined, options: { minimumFractionDigits?: number | undefined; maximumFractionDigits?: number | undefined; }) => string' is not assignable to type '(value: number) => string'.
                            valueFormatter={formatCurrency}
                            unit="/hr. +"
                            onChange={onValueChange(filterName)}
                            step={0.01}
                          />
                        );
                      } else if (filterInfo.data_type === 'date') {
                        const {min, max} = filterInfo;
                        filter = (
                          <SimpleDateRangePicker
                            // @ts-expect-error - TS2769 - No overload matches this call.
                            className={css.filter}
                            {...props}
                            value={
                              filterInfo.selected || {
                                startDate: min,
                                endDate: max,
                              }
                            }
                            minDate={min}
                            maxDate={max}
                            onChange={onValueChange(filterName)}
                          />
                        );
                      } else if (Array.isArray(filterInfo.values)) {
                        const props = {
                          classNames: {
                            outerContainer: css.selectListContainer,
                            container: css.input,
                          },
                          onChange: onValueChange(filterName),
                        } as const;
                        if (filterInfo.data_type === 'boolean') {
                          filter = (
                            // @ts-expect-error - TS2739 - Type '{ options: boolean[]; values: boolean[]; type: BooleanConstructor; classNames: { readonly outerContainer: string; readonly container: string; }; onChange: <T>(value: T) => unknown; }' is missing the following properties from type 'SenseTypeaheadProps<boolean>': resolveKey, resolveLabel
                            <SingleValueInput
                              {...props}
                              options={filterInfo.values || []}
                              values={filterInfo.selected || []}
                              type={Boolean}
                            />
                          );
                        } else if (filterInfo.data_type === 'number') {
                          filter = (
                            // @ts-expect-error - TS2739 - Type '{ options: number[]; values: number[]; type: NumberConstructor; classNames: { readonly outerContainer: string; readonly container: string; }; onChange: <T>(value: T) => unknown; }' is missing the following properties from type 'SenseTypeaheadProps<number>': resolveKey, resolveLabel
                            <SingleValueInput
                              {...props}
                              options={filterInfo.values || []}
                              values={filterInfo.selected || []}
                              type={Number}
                            />
                          );
                        } else {
                          filter = (
                            // @ts-expect-error - TS2739 - Type '{ options: string[]; values: string[]; classNames: { readonly outerContainer: string; readonly container: string; }; onChange: <T>(value: T) => unknown; }' is missing the following properties from type 'SenseTypeaheadProps<string>': resolveKey, resolveLabel
                            <StringSelectList
                              {...props}
                              options={filterInfo.values || []}
                              values={filterInfo.selected || []}
                            />
                          );
                        }
                      } else if (filterInfo.values === null) {
                        const extraProps = multiEntityEnabled
                          ? {
                              typeaheadType: 'entityAttribute',
                              typeaheadName: entityGroup,
                              typeaheadSubname: attribute_name,
                            }
                          : {
                              typeaheadType: 'attribute',
                              typeaheadName: filterName,
                            };
                        if (
                          filterInfo.data_type !== 'boolean' &&
                          filterInfo.data_type !== 'number'
                        ) {
                          filter = (
                            // @ts-expect-error - TS2322 - Type '{ classNames: { outerContainer: string; container: string; }; values: string[]; onChange: <T>(value: T) => unknown; placeholder: string; typeaheadType: string; typeaheadName: string; typeaheadSubname: string; } | { ...; }' is not assignable to type 'IntrinsicAttributes & Props'.
                            <TypeaheadSelectList
                              {...extraProps}
                              classNames={{
                                outerContainer: css.selectListContainer,
                                container: css.input,
                              }}
                              values={filterInfo.selected || []}
                              onChange={onValueChange(filterName)}
                              placeholder={t(
                                'Type_to_search',
                                'Type to search',
                              )}
                            />
                          );
                        }
                      }
                      if (!filter) {
                        return;
                      }
                      return (
                        <section className={css.section} key={display_name}>
                          <h4 className={css.subhead}>
                            {startCase(display_name)}
                          </h4>
                          {filter}
                          {editSettingsLink}
                        </section>
                      );
                    },
                  )}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </FilterCard>
  );
}

function SingleValueInput<T>(props: ValueListInputProps<T>) {
  return (
    <ValueListInput {...props} limit={1} searchable allowArbitraryValues />
  );
}
